import { useRef } from "react"
import { makeStyles } from "tss-react/mui"
import { Alert, Box, ButtonBase, Tooltip, Typography } from "@mui/material"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { Message } from "../../../createOrderTypes"

import { mapMessagePriorityToAlertSeverity } from "./helpers"
import { useInlineClampedText } from "./useInlineClampedText"

const useStyles = makeStyles()((_theme) => ({
    messageButton: {
        display: "block",
        width: "100%",
        textAlign: "left",
        borderRadius: 4,
        overflow: "hidden",
        cursor: "pointer",

        "&:focus-visible": {
            outline: "2px solid currentColor",
            outlineOffset: 2,
        },
    },
    previewContainer: {
        position: "relative",
        width: "100%",
        fontWeight: "bold",
    },
    previewText: {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        overflowWrap: "anywhere",
    },
    measurerRoot: {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        top: "-10000px",
        left: "-10000px",
    },
    measurerText: {
        overflowWrap: "anywhere",
    },
}))

type MessageCardProps = Readonly<{
    message: Message
    onClick: () => void
}>

export function MessageCard({ message, onClick }: MessageCardProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const observeSizeRef = useRef<HTMLDivElement | null>(null)
    const measureRef = useRef<HTMLParagraphElement | null>(null)
    const measurePrefixRef = useRef<HTMLSpanElement | null>(null)
    const measureSuffixRef = useRef<HTMLSpanElement | null>(null)

    const readMoreLabel = l10n.getString("create-order-drawer-message-read-more")
    const readMoreSuffix = `\u00A0${readMoreLabel}`

    const { previewText, isTruncated } = useInlineClampedText({
        fullText: message.text,
        maxLines: 2,
        suffixText: readMoreSuffix,
        measureRef,
        measurePrefixRef,
        measureSuffixRef,
        observeSizeRef,
    })

    const messageButton = (
        <ButtonBase className={classes.messageButton} onClick={onClick} disableRipple>
            <Alert severity={mapMessagePriorityToAlertSeverity(message.priority)}>
                <Box className={classes.previewContainer} ref={observeSizeRef}>
                    <Typography variant="body2" className={classes.previewText}>
                        {isTruncated ? previewText : message.text}
                        {isTruncated && <span>{readMoreSuffix}</span>}
                    </Typography>
                </Box>
            </Alert>
        </ButtonBase>
    )

    return (
        <>
            {isTruncated ? (
                <Tooltip title={message.text} placement="bottom" arrow>
                    {messageButton}
                </Tooltip>
            ) : (
                messageButton
            )}

            <Box className={classes.measurerRoot} aria-hidden>
                <Typography variant="body2" className={classes.measurerText} ref={measureRef}>
                    <span ref={measurePrefixRef} />
                    <span ref={measureSuffixRef} />
                </Typography>
            </Box>
        </>
    )
}
